import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";
import { BrandLockup } from "./LogoMark";

const LINKS = [
  { label: "Features", href: "#features", testid: "nav-link-features" },
  { label: "How it works", href: "#how", testid: "nav-link-how" },
  { label: "Screenshots", href: "#screenshots", testid: "nav-link-screenshots" },
  { label: "Safety", href: "#safety", testid: "nav-link-safety" },
  { label: "FAQ", href: "#faq", testid: "nav-link-faq" },
];

export const scrollToHash = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -84 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToHash(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
      className="fixed inset-x-0 top-0 z-50 border-b border-hairline bg-sand/85 shadow-[0_8px_30px_-18px_rgba(36,26,22,0.35)] backdrop-blur-xl"
    >
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center gap-8 px-5 sm:px-8">
        <a
          href="#top"
          data-testid="steth-logo-nav"
          onClick={(e) => go(e, "#top")}
          className="shrink-0"
        >
          <BrandLockup />
        </a>
        <div className="hidden items-center gap-7 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testid}
              onClick={(e) => go(e, l.href)}
              className="group relative text-sm font-semibold text-clay transition-colors hover:text-ink"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-[2px] w-0 rounded-full bg-terracotta transition-[width] duration-300 group-hover:w-full" />
            </a>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-3">
          <motion.a
            href="#download"
            data-testid="nav-get-app-btn"
            onClick={(e) => go(e, "#download")}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            className="hidden items-center gap-2 rounded-full bg-terracotta px-5 py-2.5 text-sm font-bold text-cream shadow-[0_10px_25px_-10px_rgba(224,122,95,0.8)] transition-colors hover:bg-terracotta-dark sm:inline-flex"
          >
            Get the app <ArrowRight size={15} />
          </motion.a>
          <button
            data-testid="nav-mobile-menu-btn"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-hairline bg-cream lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-hairline bg-sand/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`${l.testid}-mobile`}
                  onClick={(e) => go(e, l.href)}
                  className="rounded-xl px-3 py-3 text-base font-semibold text-ink hover:bg-peach/40"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#download"
                data-testid="nav-get-app-btn-mobile"
                onClick={(e) => go(e, "#download")}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-5 py-3 text-sm font-bold text-cream"
              >
                Get the app <ArrowRight size={15} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
